import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogConfig } from '@angular/material/dialog';
import { ZCValidationResultComponent } from '../zc-validation-result/zc-validation-result.component';
import { ZoningCodeService } from 'src/app/core/services/zoning-code.service';
import * as _ from 'lodash';
import { ValidationService } from 'src/app/core/services/validation.service';
import { IZoningCodesImportValidationResultModel } from 'src/app/models/calibrations/import/zoning-codes-import-validation-result.model';
import { SyncService } from 'src/app/core/services/sync.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    standalone: false,
    selector: 'zc-validation-icon',
    templateUrl: './zc-validation-icon.component.html',
    styleUrls: ['./zc-validation-icon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ZCValidationIconComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    zoningCodesImportValidationResult: IZoningCodesImportValidationResultModel;

    hasErrors = false;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly dialog: MatDialog,
        private readonly zcService: ZoningCodeService,
        private readonly syncService: SyncService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    private computeHasErrors(): boolean {
        return !this.zoningCodesImportValidationResult
            ? !this.zcService.isZCValidWithRequiredFields
            : !this.zoningCodesImportValidationResult.importValidationResult.isValid;
    }

    ngOnChanges(): void {
        this.hasErrors = this.computeHasErrors();
    }

    ngOnInit(): void {
        this.hasErrors = this.computeHasErrors();

        this.zcService.zoningCodes
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.hasErrors = this.computeHasErrors();
                this.cdr.markForCheck();
            });

        this.syncService.valueChanged
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.hasErrors = this.computeHasErrors();
                this.cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getClass() {
        return { icon: true, green: !this.hasErrors, red: this.hasErrors };
    }

    getIconType() {
        return this.hasErrors ? 'cancel' : 'check_circle';
    }

    openZCValidationPopup() {
        const validationResultTable = this.getValidationMessages();
        if (!validationResultTable) {
            return;
        }
        const dialogConfig = new MatDialogConfig();
        dialogConfig.width = '75%';
        dialogConfig.panelClass = 'popup-container';
        dialogConfig.disableClose = true;
        dialogConfig.data = validationResultTable;

        const publishDialogRef = this.dialog.open(ZCValidationResultComponent, dialogConfig);
        publishDialogRef.afterClosed().subscribe();
    }

    getValidationMessages() {
        if (this.zoningCodesImportValidationResult) {
            return this.zoningCodesImportValidationResult.importValidationResult.isValid
                ? null
                : this.zoningCodesImportValidationResult.messages;
        }
        const zcValidationResult = this.zcService.selectedZoningCode.getCachedValidationResult(false);
        if (zcValidationResult.isValid) {
            return;
        }
        return ValidationService.getZCValidationMessages(zcValidationResult);
    }
}
